import React, { useCallback, useEffect, useRef } from 'react';
import { Chart, registerables } from 'chart.js';
import 'chartjs-adapter-date-fns';
import { supabaseClient } from '../../services/supabase';
import { supabaseLocationService } from '../../services/supabaseLocationService';
import { standardLegend, standardTitle } from '../../utils/chartFormatting';

Chart.register(...registerables);

interface JobWagesBySectorTrendsProps {
  location: string;
}

// Main sectors from Census Bureau
const MAIN_SECTORS = [
  'Management, business, science, and arts occupations',
  'Service occupations',
  'Sales and office occupations',
  'Natural resources, construction, and maintenance occupations',
  'Production, transportation, and material moving occupations'
];

const SECTOR_SHORT_NAMES: Record<string, string> = {
  'Management, business, science, and arts occupations': 'Professional & Technical',
  'Service occupations': 'Service',
  'Sales and office occupations': 'Sales & Office',
  'Natural resources, construction, and maintenance occupations': 'Construction & Maintenance',
  'Production, transportation, and material moving occupations': 'Production & Transportation'
};

// Time series: median income by job sector over time
const JobWagesBySectorTrends: React.FC<JobWagesBySectorTrendsProps> = ({ location }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const chartRef = useRef<Chart | null>(null);
  const creatingRef = useRef<boolean>(false);
  const [hasData, setHasData] = React.useState<boolean | null>(null);

  const buildChart = useCallback(async () => {
    if (!canvasRef.current) return;
    if (creatingRef.current) return;
    creatingRef.current = true;

    try {
      if (chartRef.current) {
        chartRef.current.destroy();
        chartRef.current = null;
      }
      const existing = Chart.getChart(canvasRef.current as unknown as HTMLCanvasElement);
      if (existing) existing.destroy();
    } catch {}

    const ctx = canvasRef.current.getContext('2d');
    if (!ctx) return;

    await supabaseLocationService.initialize();
    const geofips = await supabaseLocationService.getGeofips(location);
    if (!geofips) {
      setHasData(false);
      creatingRef.current = false;
      return;
    }

    const geoid = String(geofips); // Define at top level for logging

    // Fetch all data - use geofips (numeric) FIRST - this is the primary column
    let { data: allData, error } = await supabaseClient
      .from('job_by_wage_level')
      .select('year, job_type, median_income, gender')
      .eq('geofips', geofips)
      .order('year', { ascending: true });

    // If no data by geofips, try geoid (text) as fallback
    if ((!allData || allData.length === 0) && !error) {
      const geoidResult = await supabaseClient
        .from('job_by_wage_level')
        .select('year, job_type, median_income, gender')
        .eq('geoid', geoid)
        .order('year', { ascending: true });
      
      if (geoidResult.data && geoidResult.data.length > 0) {
        allData = geoidResult.data;
        error = geoidResult.error;
      }
    }

    // If still no data, try location_name
    if ((!allData || allData.length === 0) && !error) {
      const nameResult = await supabaseClient
        .from('job_by_wage_level')
        .select('year, job_type, median_income, gender, geofips')
        .ilike('location_name', `%${location}%`)
        .order('year', { ascending: true });
      
      if (nameResult.data && nameResult.data.length > 0) {
        allData = nameResult.data;
        error = nameResult.error;
      }
    }

    if (error) {
      setHasData(false);
      creatingRef.current = false;
      return;
    }

    if (!allData || allData.length === 0) {
      setHasData(false);
      creatingRef.current = false;
      return;
    }

    // Filter to main sectors and gender='both' (or any if 'both' doesn't exist)
    const hasBothGender = allData.some((r: any) => (r.gender || '').toLowerCase() === 'both');
    const data = allData.filter((r: any) => {
      const jobType = (r.job_type || '').trim();
      const gender = (r.gender || '').trim();
      // Use some() to handle potential whitespace differences
      const isMainSector = MAIN_SECTORS.some(sector => sector.trim() === jobType);
      // If 'both' exists in data, only use 'both'. Otherwise, use any gender.
      const genderMatch = hasBothGender ? gender.toLowerCase() === 'both' : true;
      return isMainSector && genderMatch;
    });

    if (!data || data.length === 0) {
      setHasData(false);
      creatingRef.current = false;
      return;
    }

    setHasData(true);

    const bySector: Record<string, Record<number, number>> = {};
    const yearsSet = new Set<number>();
    (data || []).forEach((r: any) => {
      const sector = r.job_type || 'Unknown';
      const year = Number(r.year);
      const val = Number(r.median_income || 0);
      yearsSet.add(year);
      if (!bySector[sector]) bySector[sector] = {};
      bySector[sector][year] = val;
    });

    const years = Array.from(yearsSet).sort((a, b) => a - b);

    // 5 distinct colors for 5 sectors
    const colors = [
      '#1f77b4', // Blue - Professional & Technical
      '#ff7f0e', // Orange - Service
      '#2ca02c', // Green - Sales & Office
      '#d62728', // Red - Construction & Maintenance
      '#9467bd'  // Purple - Production & Transportation
    ];

    const sectors = Object.keys(bySector).sort();
    const datasets = sectors.map((sector, idx) => {
      const color = colors[idx % colors.length];
      const shortName = SECTOR_SHORT_NAMES[sector] || sector;
      return {
        label: shortName,
        data: years.map(y => bySector[sector][y] ?? null),
        borderColor: color,
        backgroundColor: 'transparent',
        tension: 0.2,
        pointRadius: 0,
        borderWidth: 2,
      } as const;
    });

    chartRef.current = new Chart(ctx, {
      type: 'line',
      data: { labels: years, datasets },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: standardLegend,
          title: standardTitle(`Median Income by Sector Over Time - ${location}`),
          tooltip: {
            mode: 'index',
            intersect: false,
            callbacks: {
              label: (ctx: any) => `${ctx.dataset.label}: $${(ctx.parsed.y || 0).toLocaleString()}`,
            }
          }
        },
        scales: {
          y: { title: { display: true, text: 'Median Income (USD)' }, beginAtZero: true },
          x: { title: { display: true, text: 'Year' } },
        },
        interaction: { mode: 'index', intersect: false },
      },
    });

    creatingRef.current = false;
  }, [location]);

  useEffect(() => {
    buildChart();
    const canvasNode = canvasRef.current;
    return () => {
      try {
        if (chartRef.current) chartRef.current.destroy();
        if (canvasNode) {
          const existing = Chart.getChart(canvasNode as unknown as HTMLCanvasElement);
          if (existing) existing.destroy();
        }
      } catch {}
    };
  }, [buildChart]);

  if (hasData === false) {
    return (
      <div className="chart-container" style={{ padding: '40px', textAlign: 'center' }}>
        <p>No job wages time series data available for {location}.</p>
      </div>
    );
  }

  return (
    <div className="chart-container">
      <canvas ref={canvasRef} style={{ height: '360px' }} />
    </div>
  );
};

export default JobWagesBySectorTrends;

